import type { LinkProps } from 'next/link';
import { SUPPORTED_CITIES } from '@/app/[city]/services/[category]/[type]/types';
import { SERVICE_AREA_DATA_MAP } from '@/app/about/our-service-areas/data';

export interface NavigationItem extends Partial<Pick<LinkProps, 'href'>> {
  label: string;
  description?: string;
  children?: NavigationItem[];
  viewAllHref?: string;
  viewAllLabel?: string;
  isCityLink?: boolean;
}

/*
 * Manually defined navigation items with descriptions
 */
export const NAVIGATION_ITEMS: NavigationItem[] = [
  {
    label: 'Services',
    viewAllHref: '/services',
    viewAllLabel: 'View All Services',
    children: [
      {
        label: 'Design & Build',
        href: '/services/design-and-build',
        description: 'Custom homes designed and built to your vision',
      },
      {
        label: 'Design & Renovate',
        href: '/services/design-and-renovate',
        description: 'Transform your existing home with expert renovation',
      },
    ],
  },
  {
    label: 'Projects',
    href: '/projects',
  },
  {
    label: 'Floor Plans',
    href: '/floor-plans',
  },
  {
    label: 'About',
    viewAllHref: '/about',
    viewAllLabel: 'View All About',
    children: [
      {
        label: 'Our Team',
        href: '/about/our-team',
        description: 'Meet the people behind Cornerstone Homes',
      },
      {
        label: 'Our Service Areas',
        href: '/about/our-service-areas',
        description: 'Discover the communities we serve',
      },
      ...SUPPORTED_CITIES.map((slug) => {
        const cityData = SERVICE_AREA_DATA_MAP[slug];
        return {
          label: cityData?.name ?? slug,
          href: `/about/our-service-areas/${slug}`,
          description: cityData?.tagline ?? '',
          isCityLink: true as const,
        };
      }),
    ],
  },
  {
    label: 'Request Consultation',
    href: '/request-consultation',
  },
];

/*
 * Extracts a flat Navigation List for Mobile
 */
export function flattenNavigationList(
  navigationItem: NavigationItem[],
): NavigationItem[] {
  return navigationItem.flatMap(({ label, href, children }) => [
    ...(href ? [{ label, href }] : []),
    ...(children ? flattenNavigationList(children) : []),
  ]);
}

/*
 * Generates the **mobile** navigation list dynamically
 */
export const MOBILE_NAVIGATION_ITEMS = flattenNavigationList(NAVIGATION_ITEMS);
